import { Input } from 'antd'
import styled from 'styled-components'

const USER_MESSAGE_BG = 'rgba(0, 0, 0, 0.25)'
const SUBTLE_BORDER = 'rgba(255, 255, 255, 0.12)'
const SUBTLE_HOVER = 'rgba(255, 255, 255, 0.08)'
const ICON_IDLE = 'rgba(255, 255, 255, 0.4)'
const ICON_HOVER_BG = 'rgba(255, 255, 255, 0.1)'
const ICON_HOVER_COLOR = 'rgba(255, 255, 255, 0.7)'
const PRIMARY_BLUE = '#3b82f6'
const PRIMARY_BLUE_BG = 'rgba(59, 130, 246, 0.15)'
const PRIMARY_BLUE_BORDER = 'rgba(59, 130, 246, 0.3)'
const PRIMARY_BLUE_HOVER = 'rgba(59, 130, 246, 0.25)'

export const MessageRow = styled.div<{ $isUser: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  align-items: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  max-width: ${({ $isUser }) => ($isUser ? '80%' : '100%')};
  align-self: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
`

export const TextMessage = styled.div<{ $isUser: boolean }>`
  color: ${({ theme }) => theme.forecast.defaultText};
  background-color: ${({ $isUser }) => ($isUser ? USER_MESSAGE_BG : 'none')};
  border: 1px solid
    ${({ $isUser }) => ($isUser ? SUBTLE_BORDER : 'transparent')};
  border-radius: 0.5rem;
  padding: 0.375rem 0.625rem;
  font-size: 0.75rem;
  overflow-wrap: break-word;
  word-break: break-word;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
`

export const UserMessageBubble = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  background-color: ${USER_MESSAGE_BG};
  border: 1px solid ${SUBTLE_BORDER};
  border-radius: 0.5rem;
  padding: 0.375rem 0.625rem;
  font-size: 0.75rem;
  overflow-wrap: break-word;
  word-break: break-word;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
`

export const StopIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  background: transparent;
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
  color: ${ICON_IDLE};
  transition:
    background 0.15s ease,
    color 0.15s ease;

  &:hover {
    background: ${ICON_HOVER_BG};
    color: ${ICON_HOVER_COLOR};
  }

  svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`

export const UserMessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
  width: 100%;
`

export const UserMessageRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
  width: 100%;
  justify-content: flex-end;
`

export const MessageActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.375rem;
  background: transparent;
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
  opacity: 0;
  transition:
    opacity 0.15s ease,
    background 0.15s ease;
  color: ${({ theme }) => theme.forecast.tertiaryText};

  &:hover {
    background: ${({ theme }) => theme.forecast.background1};
    color: ${({ theme }) => theme.forecast.secondaryText};
  }

  svg {
    width: 1rem;
    height: 1rem;
  }

  ${UserMessageRow}:hover & {
    opacity: 1;
  }
`

export const EditContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  width: 100%;
`

export const EditTextArea = styled(Input.TextArea)`
  width: 100%;
  border-radius: 0.5rem;
  padding: 0.5rem;
  background-color: ${({ theme }) => theme.forecast.background1} !important;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.75rem;

  &:focus,
  &:focus-within {
    border-color: ${({ theme }) => theme.forecast.border2} !important;
    box-shadow: none !important;
  }
`

export const EditActions = styled.div`
  display: flex;
  gap: 0.375rem;
  justify-content: flex-end;
`

export const EditActionButton = styled.button<{
  $variant?: 'primary' | 'secondary'
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.5rem;
  background: ${({ $variant }) =>
    $variant === 'primary' ? PRIMARY_BLUE_BG : 'transparent'};
  border: 1px solid
    ${({ $variant }) =>
      $variant === 'primary' ? PRIMARY_BLUE_BORDER : SUBTLE_BORDER};
  border-radius: 0.25rem;
  cursor: pointer;
  color: ${({ theme, $variant }) =>
    $variant === 'primary' ? PRIMARY_BLUE : theme.forecast.secondaryText};
  font-size: 0.625rem;
  transition: background 0.15s ease;

  &:hover {
    background: ${({ $variant }) =>
      $variant === 'primary' ? PRIMARY_BLUE_HOVER : SUBTLE_HOVER};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 0.625rem;
    height: 0.625rem;
    margin-right: 0.25rem;
  }
`

export const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.forecast.warning};
`

export const CollapsibleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 0.125rem;
  width: 100%;
`

export const CollapsibleHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.5rem;
  background: transparent;
  border: none;
  border-radius: 0.25rem;
  color: ${({ theme }) => theme.forecast.tertiaryText};
  font-size: 0.6875rem;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.forecast.background1};
  }

  svg {
    width: 0.625rem;
    height: 0.625rem;
    flex-shrink: 0;
  }
`

export const ChevronIcon = styled.span<{ $isExpanded?: boolean }>`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.forecast.tertiaryText};
  transition: transform 0.2s ease-out;
  transform: rotate(${({ $isExpanded }) => ($isExpanded ? '180deg' : '0deg')});
`

export const CollapsibleContent = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  max-height: ${({ $isExpanded }) => ($isExpanded ? '8rem' : '0')};
  overflow: hidden;
  overflow-y: ${({ $isExpanded }) => ($isExpanded ? 'auto' : 'hidden')};
  scrollbar-gutter: stable;
  transition:
    max-height 0.25s ease-out,
    opacity 0.2s ease-out;
  opacity: ${({ $isExpanded }) => ($isExpanded ? 1 : 0)};
`

export const ThoughtItemContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.25rem;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;

  &:hover {
    background: ${({ theme }) => theme.forecast.background1};
  }
`

export const ThoughtText = styled.span`
  flex: 1;
  font-size: 0.625rem;
  color: ${({ theme }) => theme.forecast.tertiaryText};
`

export const ThoughtFeedback = styled.div`
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.15s ease;

  ${ThoughtItemContainer}:hover & {
    opacity: 1;
  }
`

export const ReferenceItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;

  &:hover {
    background: ${({ theme }) => theme.forecast.background1};
  }
`

export const ReferenceContent = styled.button`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex: 1;
  min-width: 0;
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  padding: 0;

  svg {
    width: 0.6rem;
    height: 0.6rem;
    color: ${({ theme }) => theme.forecast.tertiaryText};
    flex-shrink: 0;
  }
`

export const ReferenceTitle = styled.span`
  font-size: 0.725rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const OpenInNewTabButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.125rem;
  background: transparent;
  border: none;
  border-radius: 0.125rem;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease;

  ${ReferenceItem}:hover & {
    opacity: 1;
  }

  &:hover {
    background: ${({ theme }) => theme.forecast.background1};
  }

  svg {
    width: 0.5rem;
    height: 0.5rem;
    color: ${({ theme }) => theme.forecast.tertiaryText};
  }
`

export const TabColorDot = styled.button<{ $color: string }>`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  border: none;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
  opacity: 1;
  transition: transform 0.15s ease;

  &:hover {
    transform: scale(1.3);
  }
`

export const openTooltipStyle = {
  fontSize: '0.625rem',
  padding: '0.125rem 0.375rem',
  minHeight: 'auto',
  lineHeight: 1.2,
}
